import React, { useMemo } from 'react'; 
import DOMPurify from 'dompurify'; 
import parse, { domToReact } from 'html-react-parser'; 
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { FaExclamationTriangle } from 'react-icons/fa';

/**
 * دیکد کردن HTML
 */
const decodeHtml = (html) => {
    if (typeof html !== 'string') return '';
    let decodedHtml = html.replace(/&amp;/g, '&');
    decodedHtml = decodedHtml
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"');
    return decodedHtml;
};

// ==============================================================================
// 💡 تابع اصلی: پارس و جایگزینی فرمول‌های KaTeX (نسخه اصلاح شده)
// ==============================================================================

const finalParseOptions = {
    replace: (domNode) => {
        
        // ✅✅✅ اصلاح اصلی برای رفع خطای Maximum call stack
        
        // ما فقط یک نوع نود را می‌خواهیم جایگزین کنیم:
        // نودهای متنی (type === 'text') که حاوی '$' باشند.
        
        if (domNode.type === 'text' && domNode.data && domNode.data.includes('$')) {
            
            const parts = domNode.data.split(/(\$.*?\$)/g);
            
            // اگر متنی $ داشت اما الگوی فرمول نبود (مثلاً "costs 10$")
            // به پارسر اجازه می‌دهیم کار عادی‌اش را بکند.
            if (parts.length <= 1) {
                return; // (برابر با return undefined)
            }

            // اگر فرمول KaTeX پیدا شد، آن را با کامپوننت InlineMath جایگزین کن
            return (
                <>
                    {parts.filter(part => part.length > 0).map((part, index) => {
                        if (part.startsWith('$') && part.endsWith('$')) {
                            const math = part.slice(1, -1);
                            try {
                                if (math.includes('\\frac') || math.includes('\\sum') || part.length > 50) {
                                    return (
                                        <span className="katex-display-inline" key={index}>
                                            <InlineMath math={math} />
                                        </span>
                                    );
                                }
                                return <InlineMath key={index} math={math} />;
                            } catch (error) {
                                console.error(`KaTeX Error rendering "${math}":`, error);
                                return <span key={index} className="text-danger" style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                                        <FaExclamationTriangle className="ml-1" /> [خطای فرمول]
                                    </span>;
                            }
                        }
                        // اگر بخشی از متن، فرمول نباشد
                        return <span key={index}>{part}</span>;
                    })}
                </>
            );
        }

        // برای هر نوع نود دیگری (مثل <p>, <span>, <div> و...)
        // یا نودهای متنی که '$' ندارند:
        // ما هیچ کاری نمی‌کنیم (return undefined).
        // این به html-react-parser اجازه می‌دهد تا رندر پیش‌فرض خود را انجام دهد.
        // این کار از حلقه بی‌نهایت جلوگیری می‌کند.
        return;
    },
};


// ==============================================================================
// 💡 کامپوننت نهایی ContentRenderer
// ==============================================================================
const ContentRenderer = ({ htmlContent }) => {
    
    const sanitizedHtml = useMemo(() => {
        if (!htmlContent) return '';
        
        // 1. دیکد کردن کاراکترها
        let rawHtml = decodeHtml(htmlContent);

        // 2. پنهان کردن تگ‌های [cite] (این Regex صحیح است)
        rawHtml = rawHtml.replace(/(\[cite.*?\])/g, '<span class="cite-marker">$1</span>');
        
        // 3. ایمن‌سازی HTML
        const safeHtml = DOMPurify.sanitize(rawHtml, {
            ADD_TAGS: [
                'style', 
                'span', 'table', 'tbody', 'thead', 'tr', 'td', 'th', 
                'ol', 'ul', 'li', 'strong', 'em', 'u', 'blockquote', 
                'hr', 'div', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                'br'
            ], 
            ADD_ATTR: ['class', 'style', 'dir', 'id']
        });
        
        return safeHtml;

    }, [htmlContent]);
    
    return (
        <div className="content-html">
             {parse(sanitizedHtml, finalParseOptions)} 
        </div>
    );
};

export default ContentRenderer;